import React, { useState } from 'react';
import { StarIcon, MapPinIcon, ClockIcon, UsersIcon, TagIcon, HeartIcon, ShareIcon } from 'lucide-react';
interface TourCardProps {
  tour: {
    id: string;
    title: string;
    image: string;
    category: string;
    location: string;
    rating: number;
    reviewCount: number;
    highlights: string[];
    originalPrice: number;
    currentPrice: number;
    discount: number;
    isBestSeller: boolean;
    limitedSeats: boolean;
    duration: string;
    availableSeats: number;
    slug?: string;
  };
}
export function TourCard({
  tour
}: TourCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  // Generate slug if not provided
  const slug = tour.slug || tour.title.toLowerCase().replace(/\s+/g, '-') + `-${tour.duration.split(' ')[0].toLowerCase()}days`;
  const navigateToTourDetail = (tourId: string, tourSlug: string) => {
    // Show loading state
    setIsLoading(true);
    // Use the global navigation function if available (from App.tsx)
    if (typeof window !== 'undefined' && window.navigateToTour) {
      // Log navigation attempt for debugging
      console.log(`Navigating to tour: ${tourId}, slug: ${tourSlug}`);
      // Small delay to show loading state
      setTimeout(() => {
        window.navigateToTour(tourId);
      }, 300);
    } else {
      // Fallback if the navigation function is not available
      console.error('Navigation function not available');
      setIsLoading(false);
    }
  };
  const handleCardClick = () => {
    navigateToTourDetail(tour.id, slug);
  };
  const handleViewTourClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateToTourDetail(tour.id, slug);
  };
  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };
  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use Web Share API if available
    if (navigator.share) {
      navigator.share({
        title: tour.title,
        text: `Check out this amazing tour: ${tour.title}`,
        url: `/tours/${slug}`
      }).catch(error => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support Web Share API
      const tourUrl = `${window.location.origin}/tours/${slug}`;
      navigator.clipboard.writeText(tourUrl).then(() => alert(`Tour link copied to clipboard: ${tourUrl}`)).catch(error => console.error('Failed to copy: ', error));
    }
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md hover:scale-[1.02] transition-all duration-300 cursor-pointer relative" onClick={handleCardClick} role="button" aria-label={`View details for ${tour.title}`} tabIndex={0} data-id="element-1505">
      {/* Loading overlay */}
      {isLoading && <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10" data-id="element-1506">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1507"></div>
        </div>}
      {/* Image and category */}
      <div className="relative" data-id="element-1508">
        <div className="aspect-[3/2] overflow-hidden" data-id="element-1509">
          <img src={tour.image} alt={tour.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-1510" />
        </div>
        <div className="absolute top-3 left-3" data-id="element-1511">
          <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full" data-id="element-1512">
            {tour.category}
          </span>
        </div>
        {tour.isBestSeller && <div className="absolute top-3 right-12" data-id="element-1513">
            <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full" data-id="element-1514">
              Best Seller
            </span>
          </div>}
        <div className="absolute top-3 right-3 flex space-x-1" data-id="element-1515">
          <button className={`w-8 h-8 ${isWishlisted ? 'bg-pink-100 text-pink-600' : 'bg-white/80 text-gray-600 hover:text-pink-600'} rounded-full flex items-center justify-center transition-colors`} onClick={handleWishlistClick} aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'} data-id="element-1516">
            <HeartIcon size={16} className={isWishlisted ? 'fill-pink-600' : ''} data-id="element-1517" />
          </button>
          <button className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center text-gray-600 hover:text-blue-600 transition-colors" onClick={handleShareClick} aria-label="Share tour" data-id="element-1518">
            <ShareIcon size={16} data-id="element-1519" />
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="p-4" data-id="element-1520">
        {/* Location */}
        <div className="flex items-center text-gray-600 text-sm mb-2" data-id="element-1521">
          <MapPinIcon size={14} className="mr-1" data-id="element-1522" />
          <span data-id="element-1523">{tour.location}</span>
        </div>
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 group-hover:underline transition-colors" data-id="element-1524">
          {tour.title}
        </h3>
        {/* Rating */}
        <div className="flex items-center mb-3" data-id="element-1525">
          <div className="flex mr-1" data-id="element-1526">
            {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} size={14} className={`${star <= Math.floor(tour.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-300'}`} data-id="element-1527" />)}
          </div>
          <span className="text-sm text-gray-600" data-id="element-1528">
            {tour.rating} ({tour.reviewCount} reviews)
          </span>
        </div>
        {/* Highlights */}
        <ul className="space-y-1 mb-4" data-id="element-1529">
          {tour.highlights.slice(0, 3).map((highlight, index) => <li key={index} className="flex items-start text-sm text-gray-700" data-id="element-1530">
              <span className="text-blue-600 mr-2" data-id="element-1531">•</span>
              <span className="line-clamp-1" data-id="element-1532">{highlight}</span>
            </li>)}
        </ul>
        {/* Info row */}
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4" data-id="element-1533">
          <div className="flex items-center" data-id="element-1534">
            <ClockIcon size={14} className="mr-1" data-id="element-1535" />
            <span data-id="element-1536">{tour.duration}</span>
          </div>
          {tour.limitedSeats && <div className="flex items-center text-orange-600" data-id="element-1537">
              <UsersIcon size={14} className="mr-1" data-id="element-1538" />
              <span data-id="element-1539">Only {tour.availableSeats} seats left</span>
            </div>}
        </div>
        {/* Price and button */}
        <div className="flex items-center justify-between" data-id="element-1540">
          <div data-id="element-1541">
            {tour.discount > 0 && <div className="flex items-center mb-1" data-id="element-1542">
                <span className="text-sm text-gray-500 line-through mr-2" data-id="element-1543">
                  CHF {tour.originalPrice}
                </span>
                <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded" data-id="element-1544">
                  {tour.discount}% OFF
                </span>
              </div>}
            <div className="text-lg font-bold text-gray-900" data-id="element-1545">
              CHF {tour.currentPrice}
              <span className="text-sm font-normal text-gray-600 ml-1" data-id="element-1546">
                per person
              </span>
            </div>
          </div>
          <button onClick={handleViewTourClick} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors" data-id="element-1547">
            View Tour
          </button>
        </div>
      </div>
    </div>;
}